import { BaseService, ClientError, ExtendableError, GenericPaginatedParameters, PartialBy, Unauthorized, UnexpectedError } from './base-service';
import { PaginatedResource } from '../../typings/Paginated';
import { IntervalResource } from '../../typings/Interval';

export interface GetLprDetectionsParameters extends GenericPaginatedParameters {
    plate?: string;
    cameras?: string[];
    units?: number[];
    irregularSituation?: boolean;
    isMotorcycle?: boolean;
    beginIn?: number;
    endIn?: number;
}

interface CreateReportParameters {
    name: string;
    startDate: string;
    endDate: string;
    plate?: string;
    brand?: string;
    model?: string;
    color?: string;
    year?: number;
    type?: string;
    camerasIds?: string[];
    unitNamesIds?: number[];
}

export enum PlateType {
    DENY_LIST = 'deny_list',
    ILLEGAL_DEBRIS = 'illegal_debris',
    MONITORING = 'monitoring'
}

export interface PlateData {
    id: number;
    plate: string;
    description: string;
    isActive: boolean;
    type: PlateType;
    validity?: Date;
    createdAt?: string;
    groupsIds?: number[];
    groups?: {
        id: number;
        name: string;
        description: string;
    }[];
}

export interface CreatePlateData {
    plate: string;
    description: string;
    isActive: boolean;
    type: PlateType;
    groupsIds?: number[];
}

export interface CreateMultiplePlateData {
    plates: string[];
    description: string;
    isActive: boolean;
    type: PlateType;
    groupsIds?: number[];
}

interface GetPaginatedPlatesParameters extends GenericPaginatedParameters {
    onlyActive: boolean;
    types?: PlateType[];
}

export class LPRServiceError extends ExtendableError {
    message: string;
    constructor(message: string) {
        super(message);
        this.message = message;
    }
}

interface GetLprCountReportParameters {
    cameras?: string[];
    startDate: number;
    endDate: number;
    companyId: number;
    plates?: string[];
    type: 'daily' | 'monthly';
}

class LicensePlateRecognitionService extends BaseService {
    async getPlates(filters: GetPaginatedPlatesParameters): Promise<PaginatedResource<PlateData>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/plates${this.encodeQueryParams({ ...filters })}`, {
            method: 'GET',
            headers: this.getHeaders(token)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPlate(plateId: number): Promise<PlateData> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/plate/${plateId}`, {
            method: 'GET',
            headers: this.getHeaders(token)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }


    async getCameras(companyId?: number): Promise<LprSimplifiedCamera[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/cameras${this.encodeQueryParams({ companyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 204) {
            return [];
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getDetections(params: GetLprDetectionsParameters): Promise<LprDetection[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/detections${this.encodeQueryParams({
            ...params,
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async countDetections(params: Omit<GetLprDetectionsParameters, 'page' | 'limit'>): Promise<{ count: number; }> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/detections/count${this.encodeQueryParams({
            ...params, count: true,
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }


    async getIntervalDetections(params: GetLprDetectionsParameters): Promise<IntervalResource<LprDetection>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/detections-interval${this.encodeQueryParams({
            ...params,
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getLprDetection(id: number): Promise<LprDetection> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/detection/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async editLprDetection(id: number, plate: string): Promise<LprDetection> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/detection/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ plate }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createPlate(params: CreatePlateData) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/plate`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ ...params }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'LPRServiceError') {
                throw new LPRServiceError(resJson.message);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async createMultiplePlate(params: CreateMultiplePlateData) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/multiple-plates`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ ...params }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'LPRServiceError') {
                throw new LPRServiceError(resJson.message);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async updatePlate(params: PartialBy<Omit<PlateData, 'plate' | 'type'>, 'id'>) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/plate/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

    }

    async deletePlate(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/plate/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async createReport(params: CreateReportParameters) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/report`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 204) {
            return [];
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getReports(params: GenericPaginatedParameters): Promise<PaginatedResource<PlateDetectionsReport>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/reports/paginated${this.encodeQueryParams({
            ...params,
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getReport(id: number): Promise<{ resultUrl: string; }> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/report/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getBrands(): Promise<{ brand: string; }[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/vehicle-descriptions/brands`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getTypes(): Promise<{ type: string; }[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/vehicle-descriptions/types`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getModels(): Promise<{ model: string; }[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/vehicle-descriptions/models`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getColors(): Promise<{ color: string; }[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/vehicle-descriptions/colors`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getUnitNames(): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/simplified-unit-names`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getDetectionsCountByCameraDate(params: GetLprCountReportParameters) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/license-plate-recognition/detections-count-report`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                ...params,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.blob();
    }
}

export const licensePlateRecognitionService = new LicensePlateRecognitionService();
